// src/pages/Jobs/JobForm.tsx
import React, { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import { useNavigate, useParams } from "react-router-dom";
import { get, set } from "@/lib/storage";
import { useNotifications } from "@/lib/notifications";
import { logAudit } from "@/lib/audit";
import EstimatedTonnageCard, { EstProd } from "./cards/EstimatedTonnageCard";

type Role = "Admin" | "Dispatcher" | "Loader" | "Trucker" | "Accountant";

type JobCore = {
  id: string;
  name: string;
  code?: string;
  customer?: string;
  site?: string;
  status?: "Draft" | "Active" | "Paused" | "Completed" | "Archived";
  estimatedTonnage?: number;
  startDate?: string;
  endDate?: string;
};

type People = {
  loggers?: string[];
  truckers?: string[];
  foresters?: string[];
};

type JobWithEst = JobCore &
  People & {
    createdAt?: string;
  };

type Props = {
  /** Optional mode passed from routes; used only for titles/UX. */
  mode?: "create" | "edit";
};

const SK = {
  jobsData: "jobs:data:v1",
};

const DRAFT_KEY = (id: string) => `jobs:draft:${id || "new"}:v1`;

export default function JobForm({ mode }: Props) {
  const nav = useNavigate();
  const params = useParams();
  const role = get<Role>("role", "Admin");
  const { push } = useNotifications();

  // Treat "create" as new, or when path is /jobs/new (id === 'new' or missing)
  const isNew = mode === "create" || !params.id || params.id === "new";
  const jobId = params.id && params.id !== "new" ? params.id : `J-${Math.random().toString(36).slice(2, 7)}`;

  const [job, setJob] = useState<Partial<JobWithEst>>({
    id: jobId,
    status: "Draft",
  });
  const [estRows, setEstRows] = useState<EstProd[]>([{ productType: "", value: 0 }]);
  const [rateLines, setRateLines] = useState<any[]>([]); // future: structure as needed

  // Load draft (if any)
  useEffect(() => {
    const d = get<any>(DRAFT_KEY(jobId), {});
    if (d && typeof d === "object") {
      if (d.job && typeof d.job === "object") setJob(d.job as Partial<JobWithEst>);
      if (Array.isArray(d.estRows)) setEstRows(d.estRows as EstProd[]);
      if (Array.isArray(d.rateLines)) setRateLines(d.rateLines as any[]);
    }
  }, [jobId]);

  // Persist draft as user edits
  useEffect(() => {
    set(DRAFT_KEY(jobId), { job, estRows, rateLines });
  }, [job, estRows, rateLines, jobId]);

  // Live sum to job.estimatedTonnage
  const estTotal = useMemo(
    () => estRows.reduce((a, r) => a + (Number(r.value) || 0), 0),
    [estRows]
  );

  useEffect(() => {
    setJob((j) => ({ ...j, estimatedTonnage: estTotal }));
  }, [estTotal]);

  const onSave = () => {
    const rows = get<JobWithEst[]>(SK.jobsData, []);
    const existsIdx = rows.findIndex((r) => r.id === jobId);

    const toSave: JobWithEst = {
      id: jobId,
      name: job.name || "Untitled Job",
      code: job.code,
      customer: job.customer,
      site: job.site,
      status: job.status || "Draft",
      estimatedTonnage: job.estimatedTonnage || 0,
      startDate: job.startDate,
      endDate: job.endDate,
      loggers: (job as any).loggers,
      truckers: (job as any).truckers,
      foresters: (job as any).foresters,
      createdAt: (job as any).createdAt || new Date().toISOString(),
    };

    const next =
      existsIdx >= 0
        ? (() => {
            const copy = rows.slice();
            copy[existsIdx] = { ...rows[existsIdx], ...toSave };
            return copy;
          })()
        : [...rows, toSave];

    set(SK.jobsData, next);
    set(DRAFT_KEY(jobId), null as unknown as undefined); // clear draft
    push({ title: "Job saved", body: toSave.name });
    logAudit({ action: "jobs:save", id: jobId, est: estTotal }, role);
    nav("/jobs");
  };

  return (
    <AppShell title={isNew ? "New Job" : `Edit Job: ${job.name || jobId}`}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2">
          <div className="card">
            <div className="card-header font-medium">Basics</div>
            <div className="card-body grid grid-cols-1 md:grid-cols-2 gap-2">
              <label className="flex flex-col gap-1">
                <span className="text-sm">Job Name</span>
                <input
                  className="input"
                  value={job.name ?? ""}
                  onChange={(e) => setJob((j) => ({ ...j, name: e.target.value }))}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-sm">Job Code</span>
                <input
                  className="input"
                  value={job.code ?? ""}
                  onChange={(e) => setJob((j) => ({ ...j, code: e.target.value }))}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-sm">Customer</span>
                <input
                  className="input"
                  value={job.customer ?? ""}
                  onChange={(e) => setJob((j) => ({ ...j, customer: e.target.value }))}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-sm">Site</span>
                <input
                  className="input"
                  value={job.site ?? ""}
                  onChange={(e) => setJob((j) => ({ ...j, site: e.target.value }))}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-sm">Status</span>
                <select
                  className="select"
                  value={job.status ?? "Draft"}
                  onChange={(e) =>
                    setJob((j) => ({
                      ...j,
                      status: e.target.value as JobWithEst["status"],
                    }))
                  }
                >
                  <option>Draft</option>
                  <option>Active</option>
                  <option>Paused</option>
                  <option>Completed</option>
                  <option>Archived</option>
                </select>
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="flex flex-col gap-1">
                  <span className="text-sm">Start</span>
                  <input
                    type="date"
                    className="input"
                    value={job.startDate ?? ""}
                    onChange={(e) =>
                      setJob((j) => ({ ...j, startDate: e.target.value }))
                    }
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-sm">End</span>
                  <input
                    type="date"
                    className="input"
                    value={job.endDate ?? ""}
                    onChange={(e) =>
                      setJob((j) => ({ ...j, endDate: e.target.value }))
                    }
                  />
                </label>
              </div>
            </div>
          </div>

          <EstimatedTonnageCard
            rows={estRows}
            onChange={setEstRows}
            onTotal={(sum) => setJob((j) => ({ ...j, estimatedTonnage: sum }))}
          />
        </div>

        <div className="lg:col-span-1 flex flex-col gap-3">
          <div className="card">
            <div className="card-header font-medium">People</div>
            <div className="card-body grid grid-cols-1 gap-2">
              <label className="flex flex-col gap-1">
                <span className="text-sm">Loggers (comma separated)</span>
                <input
                  className="input"
                  value={((job as any).loggers || []).join(", ")}
                  onChange={(e) =>
                    setJob((j) => ({
                      ...j,
                      loggers: e.target.value
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean),
                    } as any))
                  }
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-sm">Truckers</span>
                <input
                  className="input"
                  value={((job as any).truckers || []).join(", ")}
                  onChange={(e) =>
                    setJob((j) => ({
                      ...j,
                      truckers: e.target.value
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean),
                    } as any))
                  }
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-sm">Foresters</span>
                <input
                  className="input"
                  value={((job as any).foresters || []).join(", ")}
                  onChange={(e) =>
                    setJob((j) => ({
                      ...j,
                      foresters: e.target.value
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean),
                    } as any))
                  }
                />
              </label>
              <div className="text-sm opacity-70">
                Est. Tons:{" "}
                <span className="font-medium tabular-nums">
                  {(job.estimatedTonnage || 0).toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <button className="btn" onClick={onSave}>
              Save
            </button>
            <button className="btn btn-secondary" onClick={() => nav("/jobs")}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
